/*
  ═══════════════════════════════════════════════════════
  MONTAGE · Produktkarte mit Auswahl und Direktkauf
  ═══════════════════════════════════════════════════════
  Gehoert zu snippets/mt-karte-kauf.liquid.

  Was es tut: es liest die Auswahl der Optionsknoepfe, sucht die passende
  Variante und schreibt deren Nummer in das versteckte `id`-Feld von
  Shopifys Formular. Dazu Preis, Kaufknopf, Bild und die Beschriftung
  „Farbe: Schwarz".

  🔴 Was es NICHT tut: in den Warenkorb legen. Das macht Dawns
     product-form.js ueber `{% form 'product' %}`. Eine eigene fetch-Logik
     gegen /cart/add wuerde `product_added_to_cart` lautlos abschalten.
     Siehe EINGRIFFSREGELN.md § 2.2.

  Fortschrittliche Verbesserung: ohne dieses Skript steht auf der Karte der
  Weg zur Produktseite, nicht ein Knopf, der nichts kann. Erst hier wird
  getauscht.
  ═══════════════════════════════════════════════════════
*/

class MtKarteKauf extends HTMLElement {
  connectedCallback() {
    if (this.dataset.mtBereit) return;

    const daten = this.querySelector('[data-mt-varianten]');
    if (!daten) return;

    try {
      this.varianten = JSON.parse(daten.textContent);
    } catch (fehler) {
      // Kaputte Daten duerfen die Karte nicht mitreissen: dann bleibt der
      // Weg zur Produktseite stehen, und das ist ein brauchbarer Zustand.
      return;
    }

    this.idFeld = this.querySelector('.product-variant-id');
    this.knopf = this.querySelector('button[name="add"]');
    this.knopftext = this.querySelector('[data-mt-knopftext]');
    this.preisJetzt = this.querySelector('[data-mt-preis-jetzt]');
    this.preisVorher = this.querySelector('[data-mt-preis-vorher]');
    this.preisBlock = this.querySelector('[data-mt-preis]');
    this.markeSale = this.querySelector('[data-mt-marke-sale]');
    this.bild = this.querySelector('[data-mt-bild]');
    this.gruppen = Array.from(this.querySelectorAll('[data-mt-gruppe]'));

    const kauf = this.querySelector('[data-mt-kauf]');
    const ohneJs = this.querySelector('[data-mt-ohne-js]');
    const optionen = this.querySelector('[data-mt-optionen]');

    // Erst jetzt umschalten: ab hier kann die Karte, was sie anbietet.
    if (optionen) optionen.hidden = false;
    if (kauf) kauf.hidden = false;
    if (ohneJs) ohneJs.remove();

    this.addEventListener('change', (ereignis) => {
      if (ereignis.target.matches('[data-mt-wert]')) this.aktualisieren();
    });

    this.dataset.mtBereit = 'true';
    this.aktualisieren();
  }

  /* Die aktuell angehakten Werte, in der Reihenfolge der Optionen. */
  get auswahl() {
    return this.gruppen.map((gruppe) => {
      const gewaehlt = gruppe.querySelector('[data-mt-wert]:checked');
      return gewaehlt ? gewaehlt.value : null;
    });
  }

  varianteZu(werte) {
    return this.varianten.find((variante) =>
      werte.every((wert, stelle) => variante.optionen[stelle] === wert)
    );
  }

  aktualisieren() {
    const werte = this.auswahl;
    const variante = this.varianteZu(werte);

    this.beschriftungen(werte);
    this.werteZustand(werte);

    if (!variante) {
      // Kombination gibt es nicht. Nicht raten, welche gemeint war:
      // der Knopf sagt, dass es sie nicht gibt, und sperrt.
      if (this.idFeld) this.idFeld.disabled = true;
      this.knopfZustand(false, this.knopf && this.knopf.dataset.mtTextUnmoeglich);
      return;
    }

    this.preis(variante);
    this.bildTauschen(variante);

    if (this.idFeld) {
      this.idFeld.value = variante.id;
      this.idFeld.disabled = !variante.verfuegbar;
    }

    this.knopfZustand(variante.verfuegbar);
  }

  /* „Farbe: Schwarz" mitfuehren. */
  beschriftungen(werte) {
    this.gruppen.forEach((gruppe, stelle) => {
      const anzeige = gruppe.querySelector('[data-mt-optionswert]');
      if (anzeige && werte[stelle]) anzeige.textContent = werte[stelle];
    });
  }

  /*
    Ein Wert wird durchgestrichen, wenn er ZUSAMMEN MIT DER UEBRIGEN AUSWAHL
    zu keiner kaufbaren Variante fuehrt. Shopifys `value.available` prueft
    nur, ob es den Wert irgendwo gibt: bei Farbe x Groesse zeigt das eine
    Groesse als verfuegbar an, die es in der gewaehlten Farbe nicht gibt.
  */
  werteZustand(werte) {
    this.gruppen.forEach((gruppe, stelle) => {
      gruppe.querySelectorAll('[data-mt-wert]').forEach((feld) => {
        const probe = werte.slice();
        probe[stelle] = feld.value;
        const treffer = this.varianteZu(probe);
        const moeglich = Boolean(treffer && treffer.verfuegbar);

        feld.toggleAttribute('data-mt-aus', !moeglich);
        const hinweis = gruppe.querySelector(`label[for="${feld.id}"] [data-mt-ausverkauft-hinweis]`);
        if (hinweis) hinweis.hidden = moeglich;
      });
    });
  }

  preis(variante) {
    if (this.preisJetzt) this.preisJetzt.textContent = variante.preis;
    if (this.preisVorher) {
      this.preisVorher.textContent = variante.vorher;
      this.preisVorher.hidden = !variante.reduziert;
    }
    if (this.preisBlock) {
      this.preisBlock.classList.toggle('mt-kaufkarte__preis--reduziert', variante.reduziert);
    }
    if (this.markeSale) this.markeSale.hidden = !variante.reduziert;
  }

  /*
    Nur tauschen, wenn die Variante ein eigenes Bild hat. `srcset` faellt
    dabei weg: die Quelle ist 1200 breit und damit fuer eine 448er Karte
    auch auf feinen Bildschirmen genug. Art Direction gibt es auf einer
    Karte ohnehin nicht.
  */
  bildTauschen(variante) {
    if (!this.bild || !variante.bild) return;
    if (this.bild.getAttribute('src') === variante.bild) return;
    this.bild.removeAttribute('srcset');
    this.bild.removeAttribute('sizes');
    this.bild.setAttribute('src', variante.bild);
  }

  /*
    Die drei Beschriftungen stehen als data-Attribute am Knopf, gefuellt aus
    den Sprachdateien. Kein Text steht fest im Skript, EINGRIFFSREGELN.md § 6.
  */
  knopfZustand(kaufbar, ersatztext) {
    if (!this.knopf) return;
    this.knopf.disabled = !kaufbar;

    if (!this.knopftext) return;
    const texte = this.knopf.dataset;
    if (kaufbar) {
      this.knopftext.textContent = texte.mtTextKaufen || this.knopftext.textContent;
    } else {
      this.knopftext.textContent = ersatztext || texte.mtTextAus || this.knopftext.textContent;
    }
  }
}

if (!customElements.get('mt-karte-kauf')) {
  customElements.define('mt-karte-kauf', MtKarteKauf);
}
